// ======================================
// Customer Dashboard Page Module
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';
import { projectsAPI, quotesAPI } from '../../lib/api.js';

export function renderCustomerDashboard() {
  if (!requireAuth(['customer'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  return `
    <section class="section customer-dashboard">
      <div class="container">
        <div class="dashboard-header">
          <div class="dashboard-welcome">
            <h1>Welcome, ${user?.name || 'User'}! 👋</h1>
            <p>Your NammaVeetuInteriors dashboard</p>
          </div>
          <button class="btn btn-outline" id="logout-btn">
            ${icons.logOut || '🚪'} Logout
          </button>
        </div>
        
        <!-- Dashboard Content -->
        <div id="dashboard-content">
            <div class="text-center py-12">
                <div class="spinner text-primary text-4xl mb-4"></div>
                <p class="text-muted">Loading your projects...</p>
            </div>
        </div>
        
        <div class="dashboard-quick-links mt-12">
          <h2>Quick Links</h2>
          <div class="quick-links-grid">
            <a href="/projects" class="quick-link">
              ${icons.eye || '👁️'}
              <span>View Portfolio</span>
            </a>
            <a href="/packages" class="quick-link">
              ${icons.packages || '📦'}
              <span>Our Packages</span>
            </a>
            <a href="/contact" class="quick-link">
              ${icons.phone || '📞'}
              <span>Contact Us</span>
            </a>
            <a href="/materials" class="quick-link">
              ${icons.tool || '🔧'}
              <span>Materials</span>
            </a>
          </div>
        </div>
      </div>
    </section>
    
    <style>
      .customer-dashboard {
        min-height: 80vh;
        background: var(--color-bg-alt, #f8f9fa);
        padding: var(--space-12) 0;
        padding-top: 120px; /* Account for fixed header */
      }
      .dashboard-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: var(--space-8);
        flex-wrap: wrap;
        gap: var(--space-4);
      }
      .dashboard-welcome h1 {
        font-size: var(--text-3xl);
        margin-bottom: var(--space-2);
      }
      .dashboard-welcome p {
        color: var(--color-text-muted);
      }
      .dashboard-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: var(--space-6);
        margin-bottom: var(--space-12);
      }
      .dashboard-card {
        background: white;
        padding: var(--space-8);
        border-radius: var(--radius-xl);
        box-shadow: var(--shadow-md);
        text-align: center;
        transition: transform 0.2s;
      }
      .dashboard-card:hover {
        transform: translateY(-5px);
      }
      .dashboard-card-icon {
        font-size: 3rem;
        margin-bottom: var(--space-4);
      }
      .dashboard-card h3 {
        font-size: var(--text-xl);
        margin-bottom: var(--space-2);
      }
      .dashboard-card p {
        color: var(--color-text-muted);
        margin-bottom: var(--space-6);
      }
      .dashboard-quick-links h2 {
        font-size: var(--text-2xl);
        margin-bottom: var(--space-6);
      }
      .quick-links-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: var(--space-4);
      }
      .quick-link {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-6);
        background: white;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-sm);
        text-decoration: none;
        color: inherit;
        transition: transform 0.2s, box-shadow 0.2s;
      }
      .quick-link:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }
      .quick-link svg {
        width: 32px;
        height: 32px;
        color: var(--color-primary);
      }
      
      /* Project Card Styles */
      .project-status-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 99px;
        font-size: 0.85rem;
        font-weight: 600;
        text-transform: uppercase;
      }
      .status-quote { background: #e3f2fd; color: #1976d2; }
      .status-active { background: #e8f5e9; color: #2e7d32; }
      .status-completed { background: #fff3e0; color: #f57c00; }
    </style>
  `;
}

export async function initCustomerDashboard() {
  const logoutBtn = document.getElementById('logout-btn');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => {
      logout();
    });
  }

  await loadCustomerData();
}

async function loadCustomerData() {
  const container = document.getElementById('dashboard-content');
  if (!container) return;

  try {
    // Fetch projects
    const response = await projectsAPI.list();
    const projects = response.success ? response.data : [];

    if (projects.length > 0) {
      renderProjects(container, projects);
    } else {
      renderEmptyState(container);
    }
  } catch (error) {
    console.error('Failed to load dashboard:', error);
    container.innerHTML = `
            <div class="alert alert-error">
                Failed to load your dashboard. Please try refreshing.
            </div>
            ${renderEmptyStateHTML()} 
        `;
  }
}

function renderProjects(container, projects) {
  container.innerHTML = `
        <h2 class="text-2xl font-bold mb-6">Your Projects (${projects.length})</h2>
        <div class="dashboard-cards">
            ${projects.map(project => `
                <div class="dashboard-card text-left">
                    <div class="flex justify-between items-start mb-4">
                        <div class="dashboard-card-icon text-4xl">${project.property_type === 'Villa' ? '🏡' : '🏢'}</div>
                        <span class="project-status-badge status-${project.status}">${project.status}</span>
                    </div>
                    <h3 class="font-display">${project.title}</h3>
                    <p class="text-sm text-gray-500 mb-4">${project.address || 'No address'}</p>
                    <div class="border-t pt-4 mt-auto">
                        <div class="flex justify-between text-sm mb-2">
                            <span>Designer:</span>
                            <span class="font-medium">${project.designer_name || 'Not assigned'}</span>
                        </div>
                        <div class="flex justify-between text-sm">
                            <span>Started:</span>
                            <span class="font-medium">${new Date(project.created_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                </div>
            `).join('')}
        </div>
    `;
}

function renderEmptyState(container) {
  container.innerHTML = renderEmptyStateHTML();
}

function renderEmptyStateHTML() {
  return `
        <div class="dashboard-cards">
          <div class="dashboard-card">
            <div class="dashboard-card-icon">${icons.home || '🏠'}</div>
            <h3>No Active Projects</h3>
            <p>You don't have any projects yet. Start your interior design journey today!</p>
            <a href="/contact" class="btn btn-primary">Get Free Consultation</a>
          </div>
          
          <div class="dashboard-card">
            <div class="dashboard-card-icon">${icons.calculator || '🧮'}</div>
            <h3>Cost Estimator</h3>
            <p>Get an instant estimate for your home interior</p>
            <a href="/estimator" class="btn btn-outline">Calculate Now</a>
          </div>
        </div>
    `;
}
